import { useEffect, useRef, useState } from 'react';
import { Panel, Stack, Text } from '@/ui';
import {
  settingsCopyTextSx,
  settingsPanelSurfaceSx,
} from '@/ui/composites/settingsPageTypographySx';
import { Accordion, AccordionDetails, AccordionSummary, Fade, Tooltip } from '@/ui/primitives';
import { Box, ExpandIcon } from '@/ui/primitives';
import {
  projectSettingsAccordionDetailsSx,
  projectSettingsAccordionExpandIconSx,
  projectSettingsAccordionExpandIconWrapperSx,
  projectSettingsAccordionSummarySx,
  projectSettingsAccordionSummaryTitleStackSx,
  projectSettingsAccordionSurfaceSx,
} from './projectSettingsSurfaceSx';
import type { SpacingToken } from '@/theme/tokens';
import type { ReactElement, ReactNode, Ref } from 'react';

type ProjectSettingsAccordionProps = {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  id?: string;
  rootRef?: Ref<HTMLDivElement>;
  contentPadding?: SpacingToken;
  defaultExpanded?: boolean;
  preloadContent?: boolean;
  transitionMode?: 'auto' | 'fade' | 'none';
  disabled?: boolean;
  disabledReason?: string;
  forceExpanded?: boolean;
  onExpanded?: () => void;
};

export const ProjectSettingsAccordion = ({
  title,
  description,
  children,
  id,
  rootRef,
  contentPadding = 'none',
  defaultExpanded = false,
  preloadContent = false,
  transitionMode = 'auto',
  disabled = false,
  disabledReason,
  forceExpanded = false,
  onExpanded,
}: ProjectSettingsAccordionProps): ReactElement => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [hasExpanded, setHasExpanded] = useState(defaultExpanded);
  const hasReportedExpansionRef = useRef(false);

  useEffect(() => {
    if (!disabled) return;
    setExpanded(false);
  }, [disabled]);

  useEffect(() => {
    if (disabled || !forceExpanded) return;
    setExpanded(true);
    setHasExpanded(true);
  }, [disabled, forceExpanded]);

  useEffect(() => {
    if (!expanded) return;
    setHasExpanded(true);
    if (!hasReportedExpansionRef.current) {
      hasReportedExpansionRef.current = true;
      onExpanded?.();
    }
  }, [expanded, onExpanded]);

  return (
    <Accordion
      id={id}
      ref={rootRef}
      disableGutters
      elevation={0}
      square
      expanded={expanded}
      slots={transitionMode === 'fade' ? { transition: Fade } : undefined}
      slotProps={{
        transition: {
          mountOnEnter: !preloadContent,
          unmountOnExit: !preloadContent,
          ...(transitionMode === 'none'
            ? { timeout: 0 }
            : transitionMode === 'fade'
              ? { timeout: { enter: 180, exit: 120 } }
              : {}),
          ...(transitionMode === 'none'
            ? {}
            : transitionMode === 'fade'
              ? {}
              : {
                  easing: {
                    enter: 'cubic-bezier(0.2, 0, 0, 1)',
                    exit: 'cubic-bezier(0.4, 0, 1, 1)',
                  },
                }),
        },
      }}
      onChange={(event, nextExpanded) => {
        if (disabled) {
          return;
        }
        const target = event.target as HTMLElement | null;
        if (!target?.closest('.MuiAccordionSummary-expandIconWrapper')) {
          return;
        }
        setExpanded(nextExpanded);
      }}
      sx={[settingsPanelSurfaceSx, projectSettingsAccordionSurfaceSx]}
    >
      <AccordionSummary
        expandIcon={
          <Tooltip
            title={disabled ? (disabledReason ?? 'Enable metrics to configure thresholds.') : ''}
            disableHoverListener={!disabled}
            arrow
          >
            <Box component="span" sx={projectSettingsAccordionExpandIconWrapperSx}>
              <ExpandIcon fontSize="medium" sx={projectSettingsAccordionExpandIconSx} />
            </Box>
          </Tooltip>
        }
        sx={projectSettingsAccordionSummarySx(disabled)}
      >
        <Stack gap="xs" sx={projectSettingsAccordionSummaryTitleStackSx}>
          <Text role="sectionTitle" color={disabled ? 'textSecondary' : undefined}>
            {title}
          </Text>
          {description ? (
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              {description}
            </Text>
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={projectSettingsAccordionDetailsSx}>
        {preloadContent || expanded || hasExpanded ? (
          <Panel container="none" padding={contentPadding}>
            {children}
          </Panel>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
};
